import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { config } from '../../../Helpers/env';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TextField from '../../Utils/FormInputs/TextField';
import { titleCase } from '../../Users/addUser';

const useStyles = makeStyles(() => ({
  root: { padding: 16 },
  title: { fontSize: '1rem', fontWeight: 600, color: '#333', marginBottom: 12 },
  tableContainer: {
    '& .MuiTableCell-root': { padding: '8px 12px', fontSize: '0.85rem' },
    '& .MuiTableCell-head': {
      fontWeight: 600,
      color: '#666',
      backgroundColor: '#FAFAFA',
      fontSize: '0.8rem',
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      whiteSpace: 'nowrap',
    },
  },
}));

const makeTimestamp = () => {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
};

const VitalsTimeline = ({ onChange, initialData = null }) => {
  const classes = useStyles();
  const [metrics, setMetrics] = useState([]);
  const [timestamp, setTimestamp] = useState(makeTimestamp());
  const [values, setValues] = useState({});

  // Keep a ref so the metrics effect can read the latest initialData
  // even if it arrives before metrics finish loading
  const initialDataRef = useRef(initialData);
  initialDataRef.current = initialData;

  // Fetch triage metrics once on mount
  useEffect(() => {
    axios.get(`${config.smsUrl}/cvisit/retrieve_all_metrics`)
      .then((res) => {
        const all = res.data?.metrics || (Array.isArray(res.data) ? res.data : []);
        const vital = all.filter((m) => m.metric_id > 2);
        setMetrics(vital);

        const initial = {};
        vital.forEach((m) => { initial[m.metric_id] = ''; });

        // If initialData already arrived before metrics, overlay it now
        const initD = initialDataRef.current;
        if (initD && Array.isArray(initD.metrics) && initD.metrics.length > 0) {
          initD.metrics.forEach((m) => { initial[m.metric_id] = String(m.metric_value ?? ''); });
          const ts = initD.timestamp || makeTimestamp();
          setTimestamp(ts);
          setValues(initial);
          if (onChange) onChange({ timestamp: ts, values: initial });
        } else {
          setValues(initial);
          if (onChange) onChange({ timestamp: makeTimestamp(), values: initial });
        }
      })
      .catch(() => {});
  }, []);

  // If initialData arrives AFTER metrics have already loaded, apply it
  useEffect(() => {
    if (!initialData || !Array.isArray(initialData.metrics) || initialData.metrics.length === 0) return;
    // If metrics not loaded yet (values is empty), the metrics effect will handle it via ref
    setValues((prev) => {
      if (Object.keys(prev).length === 0) return prev;
      const updated = { ...prev };
      initialData.metrics.forEach((m) => { updated[m.metric_id] = String(m.metric_value ?? ''); });
      return updated;
    });
    const ts = initialData.timestamp || makeTimestamp();
    setTimestamp(ts);
    if (onChange) onChange({
      timestamp: ts,
      values: (() => {
        const snap = {};
        initialData.metrics.forEach((m) => { snap[m.metric_id] = String(m.metric_value ?? ''); });
        return snap;
      })(),
    });
  }, [initialData]);

  const handleTimestampChange = (e) => {
    const value = e.target.value;
    setTimestamp(value);
    if (onChange) onChange({ timestamp: value, values });
  };

  const handleValueChange = (metricId) => (e) => {
    const value = e.target.value;
    setValues((prev) => {
      const updated = { ...prev, [metricId]: value };
      if (onChange) onChange({ timestamp, values: updated });
      return updated;
    });
  };

  return (
    <Paper className={classes.root} elevation={0} variant="outlined">
      <Typography className={classes.title}>Vitals Monitoring</Typography>

      <TableContainer className={classes.tableContainer}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Time</TableCell>
              {metrics.map((m) => (
                <TableCell key={m.metric_id}>
                  {titleCase(m.metric_name)}{m.uom ? ` (${m.uom})` : ''}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell style={{ minWidth: 110 }}>
                <TextField type="time" value={timestamp} onChange={handleTimestampChange} />
              </TableCell>
              {metrics.map((m) => (
                <TableCell key={m.metric_id} style={{ minWidth: 90 }}>
                  <TextField
                    type="number"
                    value={values[m.metric_id] || ''}
                    onChange={handleValueChange(m.metric_id)}
                    min={0}
                  />
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default VitalsTimeline;
